import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import Api from '../utils/Api';
import { useAuth } from './AuthContext';
import { useError } from './ErrorContext';
import { useLoading } from './LoadingContext';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useRoutePath } from '../utils/getRoutePath';

const EstimationContext = createContext();
 
export const useEstimations = () => {
  const context = useContext(EstimationContext);
  if (!context) {
    throw new Error('useEstimations must be used within an EstimationProvider');
  }
  return context;
};

export const EstimationProvider = ({ children }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { hideLoading } = useLoading();
  const { getToken } = useAuth();
  const [ setApi] = useState(null);
  const { handleApiError, formErrors, clearErrors } = useError();
  const [estimations, setEstimations] = useState([]);
  const segments = location.pathname.split('/');
  const id = segments.pop();
  const [paginate, setPaginate] = useState(true);
  const getRoutePath = useRoutePath();
  const initialized = location.pathname === getRoutePath('/estimations');

  const getEstimations = useCallback(async (page = 1, filters = {}) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
   
    try {
      const searchParams = new URLSearchParams(window.location.search);
      let query = '';
      if(filters?.q){
        query = '&q='+filters?.q;
        
      } else{
        const q = searchParams.get('q') || '';
        query = '&q='+q;
      }
      if(filters?.active){
        query += '&active='+filters?.active;
      } else{
        const active = searchParams.get('active') || '';
        query += '&active='+active;
      }
      let response = await apiInstance.call('estimations?page=' + page+'&paginate='+paginate +''+query , 'GET');
      if(filters?.all){
        query = '';
        setPaginate(false)
        response = await apiInstance.call('estimations');  
      }
      
      if (response.status === 200) {
        setPaginate(response.data.paginate)
        setEstimations(response.data.estimations || []);
       
        
        if(filters){
          return response.data.estimations;
        }
      } else {
        
        handleApiError(response.error, {
          showAlert: false,
          redirectOn401: true,
          customMessage: 'Failed to fetch estimations information'
        });
      }
    } catch (error) {
     
    } finally {
      
    }
  }, [handleApiError, paginate, getToken]);

  const getEstimationByIdApi = useCallback(async (id, filters = {}) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call(`estimations/${id}`, 'GET', null, true, false);
   
    if (response.status === 200) {
      
      let estimations = {'data':[response.data.estimation]}
      setEstimations(estimations || []);
      
    } 
  }, [getToken]);

  useEffect(() => {
    
    const handleLoad = async() => {
      const currentPath = window.location.pathname;
      
      if(currentPath.includes(getRoutePath('/estimations/edit/'+id))){
        if (getToken()) {
        
          
          await getEstimationByIdApi(id);
        }
      }
    };

    // Add onload listener
    window.addEventListener('load', handleLoad);

    // Cleanup listener
    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, [getRoutePath, id, getToken, getEstimationByIdApi, searchParams,setApi]);

  // Initialize API and load initial data
  useEffect(() => {
    const initializeApi = async () => {
      try {
     
        if (getToken()) {
          let page = parseInt(searchParams.get('page')) || 1;
         
          await getEstimations(page);
        }
      } catch (error) {
        hideLoading();
      }
    };
   
    if(initialized){
      initializeApi();
    }
  }, [getToken, getEstimations, searchParams, initialized,hideLoading,setApi]);

  const getEstimationAdditionalData = useCallback(async () => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call('get-estimation-additional-data', 'GET', null, true);
    if(response.status === 200){
       return response.data;
    } else{
      return handleApiError(response.error, {
        showAlert: false,
        redirectOn401: true,
        customMessage: 'Failed to get estimation data'
      });
    } 
  }, [getToken, handleApiError]);

  const addEstimation = async (data) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {

      let formData = new FormData();

      // Append documents (files) - both uploaded and selected documents
      const allDocuments = [
        ...(data.documents || []),
        ...(data.selected_docs || [])
      ];
      
      allDocuments.forEach((item, index) => {
        formData.append(`documents[${index}][id]`, item.id);
        formData.append(`documents[${index}][file]`, item.file || null);
        formData.append(`documents[${index}][name]`, item.name);
        formData.append(`documents[${index}][size]`, item.size || '');
        formData.append(`documents[${index}][type]`, item.type || '');
        formData.append(`documents[${index}][url]`, item.url || '');
        formData.append(`documents[${index}][created_at]`, item.created_at || '');
      });
      
      // Append pictures (files)
      if (data.pictures) {
        data.pictures.forEach((item, index) => {
          formData.append(`pictures[${index}][id]`, item.id);
          formData.append(`pictures[${index}][file]`, item.file);
          formData.append(`pictures[${index}][name]`, item.name);
          formData.append(`pictures[${index}][size]`, item.size);
          formData.append(`pictures[${index}][type]`, item.type);
          formData.append(`pictures[${index}][created_at]`, item.created_at);
         
        });
      }

      // Append receipts from expenses (files)
      let receiptIndex = 0;
      if (data.expenses) {
        data.expenses.forEach((expense, expenseIndex) => {
          if (expense.receipts && Array.isArray(expense.receipts)) {
            expense.receipts.forEach((receipt) => {
              if (receipt.file) {
                formData.append(`receipts[${receiptIndex}][expense_id]`, expense.id);
                formData.append(`receipts[${receiptIndex}][id]`, receipt.id);
                formData.append(`receipts[${receiptIndex}][file]`, receipt.file);
                formData.append(`receipts[${receiptIndex}][name]`, receipt.name);
                formData.append(`receipts[${receiptIndex}][size]`, receipt.size);
                formData.append(`receipts[${receiptIndex}][type]`, receipt.type);
                formData.append(`receipts[${receiptIndex}][created_at]`, receipt.created_at);
                receiptIndex++;
              }
            });
          }
        });
      }


      
      // Remove documents, pictures, and receipts from the original object so we can serialize the rest
      const dataCopy = { ...data };
      delete dataCopy.documents;
      delete dataCopy.pictures;
       
      // Remove receipt file objects from expenses to prevent duplicates
      if (dataCopy.expenses) {
        dataCopy.expenses.forEach((expense) => {
          if (expense.receipts && Array.isArray(expense.receipts)) {
            expense.receipts = expense.receipts.filter(receipt => !receipt.file);
          }
        });
      }
      
      // Append the rest of the data as JSON
      formData.append('data', JSON.stringify(dataCopy));

      const response = await apiInstance.call('estimations', 'POST', formData, true,true);
      
      if (response.status === 201 || response.status === 200) {
        // Refresh the estimations list
        await getEstimations();
        return { status: response.status, data: response.data };
      } else {
        if (response.error.status === 422) {
          return {
            status: response.error.status,
            errors: response.error.validationErrors
          };
        } else {
          // Only call handleApiError for non-validation errors
          handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to add Estimation'
          });
          return {
            status: response.error.status,
            error: response.error
          };
        }
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to create estimation'
      });
      return { status: 500, error: error.message };
    }
  };

  const getEstimation = (id) => {
    return estimations?.data?.find(estimation => Number(estimation.id) === Number(id));
  };

  const getEstimationById = useCallback(async (id) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const response = await apiInstance.call(`estimations/${id}`, 'GET', null, true);
      
      if (response.status === 200) {
        return { status: response.status, data: response.data.estimation };
      } else {
        handleApiError(response.error, {
          showAlert: true,
          redirectOn401: true,
          customMessage: 'Failed to fetch estimation details'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to fetch estimation details'
      });
      return { status: 500, error: error.message };
    }
  }, [getToken, handleApiError]);

  const updateEstimation = async (id, updatedData) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      let formData = new FormData();

      // Append documents (files)
      const allDocuments = [
        ...(updatedData.documents || []),
        ...(updatedData.selected_docs || [])
      ];
      
      allDocuments.forEach((item, index) => {
        formData.append(`documents[${index}][id]`, item.id);
        formData.append(`documents[${index}][file]`, item.file);
        formData.append(`documents[${index}][name]`, item.name);
        formData.append(`documents[${index}][size]`, item.size || '');
        formData.append(`documents[${index}][type]`, item.type || '');
        formData.append(`documents[${index}][created_at]`, item.created_at || '');
      });

      
      // Append pictures (files)
      if (updatedData.pictures) {
        updatedData.pictures.forEach((item, index) => {
          formData.append(`pictures[${index}][id]`, item.id);
          formData.append(`pictures[${index}][file]`, item.file);
          formData.append(`pictures[${index}][name]`, item.name);
          formData.append(`pictures[${index}][size]`, item.size);
          formData.append(`pictures[${index}][type]`, item.type);
          formData.append(`pictures[${index}][created_at]`, item.created_at);
         
        });
      }

      // Append receipts from expenses (files)
      let receiptIndex = 0;
      if (updatedData.expenses) {
        updatedData.expenses.forEach((expense, expenseIndex) => {
          if (expense.receipts && Array.isArray(expense.receipts)) {
            expense.receipts.forEach((receipt) => {
              if (receipt.file) {
                formData.append(`receipts[${receiptIndex}][expense_id]`, expense.id);
                formData.append(`receipts[${receiptIndex}][id]`, receipt.id);
                formData.append(`receipts[${receiptIndex}][file]`, receipt.file);
                formData.append(`receipts[${receiptIndex}][name]`, receipt.name);
                formData.append(`receipts[${receiptIndex}][size]`, receipt.size);
                formData.append(`receipts[${receiptIndex}][type]`, receipt.type);
                formData.append(`receipts[${receiptIndex}][created_at]`, receipt.created_at);
                receiptIndex++;
              }
            });
          }
        });
      }


      
      // Remove documents, pictures, and receipts from the original object so we can serialize the rest
      const dataCopy = { ...updatedData };
      delete dataCopy.documents;
      delete dataCopy.pictures;
       
      // Remove receipt file objects from expenses to prevent duplicates
      if (dataCopy.expenses) {
        dataCopy.expenses.forEach((expense) => {
          if (expense.receipts && Array.isArray(expense.receipts)) {
            expense.receipts = expense.receipts.filter(receipt => !receipt.file);
          }
        });
      }
      
      // Append the rest of the data as JSON
      formData.append('data', JSON.stringify(dataCopy));
      
      // Now send the request with formData only once
      const response = await apiInstance.call(`estimations/${id}`, 'POST', formData, true, true);
      
      if (response.status === 200) {
        // Refresh the estimations list
        await getEstimations();
        return { status: response.status, data: response.data };
      } else {
        if (response.error.status === 422) {
          return {
            status: response.error.status,
             errors: response.error.validationErrors,
          };
        } else {
          // Only call handleApiError for non-validation errors
          handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to update Estimation'
          });
          return {
            status: response.error.status,
            error: response.error
          };
        }
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to update estimation'
      });
      return { status: 500, error: error.message };
    }
  };

  const deleteEstimation = async (id) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const response = await apiInstance.call(`estimations/${id}`, 'DELETE', null, true);
      
      if (response.status === 200) {
        // Refresh the estimations list
        await getEstimations();
        navigate(getRoutePath('/estimations'));
        return { status: response.status, data: response.data };
      } else {
        handleApiError(response.error, {
          showAlert: true,
          redirectOn401: true,
          customMessage: 'Failed to delete estimation'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to delete estimation'
      });
      return { status: 500, error: error.message };
    }
  };

  return (
    <EstimationContext.Provider
      value={{
        estimations,
        addEstimation,
        getEstimation,
        getEstimationById,
        updateEstimation,
        deleteEstimation,
        getEstimations,
        getEstimationAdditionalData,
        formErrors,
        clearErrors
      }}
    >
      {children}
    </EstimationContext.Provider>
  );
};

export default EstimationContext; 